// IG Marketing review page.
//
// List of n8n-generated drafts as compact rows; click View to open a modal
// where copy can be edited, the image can be regenerated via the
// godrop-media-service, replaced with a manual upload, the post deleted, or
// flipped to approved/rejected. Once approved the n8n Auto Publisher (every
// 15 min) picks it up and posts to @godrop_sg.

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Check,
  X,
  RefreshCw,
  Camera,
  ExternalLink,
  Trash2,
  Upload,
  Save,
  Loader2,
  ChevronRight,
} from 'lucide-react';
import { api, ApiError } from '../api';
import { Shell } from '../components/layout/Shell';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { EmptyState } from '../components/ui/empty-state';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useToast } from '../lib/toast';
import { fmtRelative } from '../lib/utils';

type Status = 'draft' | 'approved' | 'published' | 'failed';

interface PostRow {
  id: string;
  idea_id: string | null;
  title: string | null;
  caption: string | null;
  hashtags: string | null;
  visual_brief: string | null;
  post_type: string | null;
  media_url: string | null;
  status: Status;
  post_at: string | null;
  ig_media_id: string | null;
  ig_permalink: string | null;
  created_at: string;
  updated_at: string;
  source: string | null;
  source_url: string | null;
  angle: string | null;
  audience: string | null;
  relevance_score: number | null;
}

const STATUS_BADGE: Record<Status, 'default' | 'open' | 'accepted' | 'completed' | 'cancelled' | 'urgent' | 'brand'> = {
  draft: 'open',
  approved: 'accepted',
  published: 'completed',
  failed: 'urgent',
};

export function IgPostsPage() {
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') ?? 'draft') as Status;

  const [rows, setRows] = useState<PostRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const { show: toast } = useToast();

  async function load() {
    setRows(null);
    setErr(null);
    try {
      const r = await api<{ posts: PostRow[] }>(
        `/api/admin/ig-posts?status=${encodeURIComponent(status)}`,
      );
      setRows(r.posts);
    } catch (e) {
      setErr(e instanceof ApiError ? `api ${e.status}` : String(e));
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const openPost = rows?.find((p) => p.id === openId) ?? null;

  return (
    <Shell title="IG Marketing">
      <PageHeader
        title="IG Marketing"
        description="Review n8n-generated drafts. Approve to queue for Auto Publisher (every 15 min)."
      />

      <Tabs
        value={status}
        onValueChange={(v) => {
          const next = new URLSearchParams(params);
          next.set('status', v);
          setParams(next, { replace: true });
          setOpenId(null);
        }}
        className="mb-4"
      >
        <TabsList>
          <TabsTrigger value="draft">Draft</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="published">Published</TabsTrigger>
          <TabsTrigger value="failed">Failed</TabsTrigger>
        </TabsList>
      </Tabs>

      {err && (
        <Card className="mb-4 border-error/40 bg-error-tint">
          <CardContent className="p-4 text-sm text-error-on-tint">{err}</CardContent>
        </Card>
      )}

      {rows === null ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Camera}
          title={`No ${status} posts`}
          description={
            status === 'draft'
              ? 'Run the n8n Writer workflow to generate drafts.'
              : `No posts currently in ${status} state.`
          }
        />
      ) : (
        <Card>
          <ul className="divide-y divide-border">
            {rows.map((post) => (
              <PostListRow key={post.id} post={post} onOpen={() => setOpenId(post.id)} />
            ))}
          </ul>
        </Card>
      )}

      <Dialog
        open={openPost !== null}
        onOpenChange={(o) => {
          if (!o) setOpenId(null);
        }}
      >
        <DialogContent className="max-w-2xl p-0">
          {openPost && (
            <PostDetail
              post={openPost}
              onClose={() => setOpenId(null)}
              onChanged={load}
              toast={toast}
            />
          )}
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// List row
// ---------------------------------------------------------------------------

function PostListRow({ post, onOpen }: { post: PostRow; onOpen: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-surface-muted focus-visible:bg-surface-muted focus-visible:outline-none"
      >
        <div className="h-20 w-16 flex-shrink-0 overflow-hidden rounded-md bg-surface-muted">
          {post.media_url ? (
            <img
              src={post.media_url}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[10px] text-text-muted">
              no img
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            <Badge variant={STATUS_BADGE[post.status]}>{post.status}</Badge>
            {post.post_type && (
              <span className="text-xs text-text-muted">{post.post_type}</span>
            )}
            {post.relevance_score !== null && (
              <span className="text-xs text-text-muted">
                · score {post.relevance_score}/10
              </span>
            )}
            <span className="ml-auto text-xs text-text-muted">
              {fmtRelative(post.created_at)}
            </span>
          </div>
          <p className="line-clamp-2 text-sm text-text">
            {post.caption || (
              <span className="italic text-text-muted">no caption</span>
            )}
          </p>
          {post.hashtags && (
            <p className="mt-1 line-clamp-1 text-xs text-text-muted">{post.hashtags}</p>
          )}
        </div>

        <ChevronRight className="h-4 w-4 flex-shrink-0 text-text-muted" />
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Detail modal
// ---------------------------------------------------------------------------

type Toast = (msg: string, variant?: 'success' | 'error' | 'info') => void;

interface DetailProps {
  post: PostRow;
  onClose: () => void;
  onChanged: () => Promise<void>;
  toast: Toast;
}

function PostDetail({ post, onClose, onChanged, toast }: DetailProps) {
  const [caption, setCaption] = useState(post.caption ?? '');
  const [hashtags, setHashtags] = useState(post.hashtags ?? '');
  const [mediaUrl, setMediaUrl] = useState(post.media_url ?? '');
  const [busy, setBusy] = useState<null | 'save' | 'approve' | 'reject' | 'delete' | 'regen' | 'upload'>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset editor state when switching posts inside the same modal session.
  useEffect(() => {
    setCaption(post.caption ?? '');
    setHashtags(post.hashtags ?? '');
    setMediaUrl(post.media_url ?? '');
  }, [post.id]);

  const dirty =
    caption !== (post.caption ?? '') || hashtags !== (post.hashtags ?? '');

  async function persist(extra: Partial<PostRow> = {}) {
    const body: Partial<PostRow> = { ...extra };
    if (caption !== (post.caption ?? '')) body.caption = caption;
    if (hashtags !== (post.hashtags ?? '')) body.hashtags = hashtags;
    if (Object.keys(body).length === 0) return true;
    try {
      await api(`/api/admin/ig-posts/${post.id}`, { method: 'PATCH', json: body });
      return true;
    } catch (e) {
      toast(e instanceof ApiError ? `api ${e.status}` : String(e), 'error');
      return false;
    }
  }

  async function onSave() {
    setBusy('save');
    const ok = await persist();
    setBusy(null);
    if (ok) {
      toast('Saved', 'success');
      await onChanged();
    }
  }

  async function onApprove() {
    if (!mediaUrl) {
      toast('Approve needs an image — regenerate or upload one first', 'error');
      return;
    }
    setBusy('approve');
    const ok = await persist({ status: 'approved' });
    setBusy(null);
    if (ok) {
      toast('Approved — queued for Auto Publisher', 'success');
      await onChanged();
      onClose();
    }
  }

  async function onReject() {
    setBusy('reject');
    const ok = await persist({ status: 'failed' });
    setBusy(null);
    if (ok) {
      toast('Rejected', 'success');
      await onChanged();
      onClose();
    }
  }

  async function onDelete() {
    if (!confirm('Delete this draft permanently?')) return;
    setBusy('delete');
    try {
      await api(`/api/admin/ig-posts/${post.id}`, { method: 'DELETE' });
      toast('Deleted', 'success');
      await onChanged();
      onClose();
    } catch (e) {
      toast(e instanceof ApiError ? `api ${e.status}` : String(e), 'error');
    } finally {
      setBusy(null);
    }
  }

  async function onRegen() {
    if (!post.visual_brief) {
      toast('No visual_brief on this post — can’t regen', 'error');
      return;
    }
    setBusy('regen');
    try {
      const r = await api<{ job_id: string }>(
        `/api/admin/ig-posts/${post.id}/regenerate`,
        { method: 'POST' },
      );
      const finalUrl = await pollJob(post.id, r.job_id);
      setMediaUrl(finalUrl);
      toast('New image ready', 'success');
      await onChanged();
    } catch (e) {
      toast(e instanceof ApiError ? `api ${e.status}` : String(e), 'error');
    } finally {
      setBusy(null);
    }
  }

  async function onUploadFile(file: File) {
    setBusy('upload');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api<{ media_url: string }>(
        `/api/admin/ig-posts/${post.id}/upload-media`,
        { method: 'POST', body: fd },
      );
      setMediaUrl(r.media_url);
      toast('Uploaded', 'success');
      await onChanged();
    } catch (e) {
      toast(e instanceof ApiError ? `api ${e.status}` : String(e), 'error');
    } finally {
      setBusy(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  const isRegen = busy === 'regen';

  return (
    <div className="flex max-h-[85vh] flex-col">
      <DialogHeader className="border-b border-border p-4">
        <div className="flex items-center gap-2">
          <DialogTitle className="text-base">Edit IG post</DialogTitle>
          <Badge variant={STATUS_BADGE[post.status]}>{post.status}</Badge>
          {post.post_type && (
            <span className="text-xs text-text-muted">{post.post_type}</span>
          )}
          {post.audience && (
            <span className="text-xs text-text-muted">· {post.audience}</span>
          )}
          {post.relevance_score !== null && (
            <span className="text-xs text-text-muted">
              · score {post.relevance_score}/10
            </span>
          )}
        </div>
      </DialogHeader>

      <div className="grid flex-1 overflow-y-auto md:grid-cols-[1fr_1fr]">
        <div className="relative bg-surface-muted">
          {mediaUrl ? (
            <a href={mediaUrl} target="_blank" rel="noreferrer">
              <img
                src={mediaUrl}
                alt=""
                className="aspect-[4/5] w-full object-cover"
              />
            </a>
          ) : (
            <div className="flex aspect-[4/5] w-full items-center justify-center text-sm text-text-muted">
              No image
            </div>
          )}
          {isRegen && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 text-white">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="text-sm">Regenerating… (~30-60s)</span>
            </div>
          )}
        </div>

        <div className="space-y-3 p-4">
          <div>
            <label className="text-xs font-medium text-text-muted">Caption</label>
            <textarea
              className="mt-1 w-full resize-y rounded-md border border-border bg-bg p-2 text-sm text-text focus:border-border-emphasis focus:outline-none"
              rows={8}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-text-muted">Hashtags</label>
            <textarea
              className="mt-1 w-full resize-y rounded-md border border-border bg-bg p-2 text-sm text-text focus:border-border-emphasis focus:outline-none"
              rows={3}
              value={hashtags}
              onChange={(e) => setHashtags(e.target.value)}
            />
          </div>

          {post.visual_brief && (
            <details className="text-xs text-text-muted">
              <summary className="cursor-pointer">visual_brief</summary>
              <p className="mt-1 whitespace-pre-wrap">{post.visual_brief}</p>
            </details>
          )}

          {post.source_url && (
            <p className="text-xs text-text-muted">
              Source:{' '}
              <a
                href={post.source_url}
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-text"
              >
                {post.source}
              </a>
            </p>
          )}

          {post.ig_permalink && (
            <a
              href={post.ig_permalink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-text underline"
            >
              Open on Instagram <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void onUploadFile(f);
        }}
      />

      <div className="flex flex-wrap items-center gap-2 border-t border-border p-4">
        <Button
          size="sm"
          variant="secondary"
          onClick={onRegen}
          disabled={busy !== null || !post.visual_brief}
          title={!post.visual_brief ? 'No visual_brief on this post' : 'Generate a new kie.ai image'}
        >
          <RefreshCw className={`mr-1 h-4 w-4 ${isRegen ? 'animate-spin' : ''}`} />
          Regenerate
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy !== null}
        >
          <Upload className="mr-1 h-4 w-4" />
          Upload
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onDelete}
          disabled={busy !== null}
          className="text-error hover:bg-error-tint hover:text-error-on-tint"
        >
          <Trash2 className="mr-1 h-4 w-4" />
          Delete
        </Button>

        <div className="ml-auto flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={onSave}
            disabled={busy !== null || !dirty}
          >
            <Save className="mr-1 h-4 w-4" />
            Save
          </Button>
          {post.status === 'draft' && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={onReject}
                disabled={busy !== null}
              >
                <X className="mr-1 h-4 w-4" />
                Reject
              </Button>
              <Button
                size="sm"
                onClick={onApprove}
                disabled={busy !== null || !mediaUrl}
                title={!mediaUrl ? 'Add an image before approving' : undefined}
              >
                <Check className="mr-1 h-4 w-4" />
                Approve
              </Button>
            </>
          )}
          {post.status === 'failed' && (
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                setBusy('save');
                const ok = await persist({ status: 'draft' });
                setBusy(null);
                if (ok) {
                  await onChanged();
                  onClose();
                }
              }}
              disabled={busy !== null}
            >
              <RefreshCw className="mr-1 h-4 w-4" />
              Reopen as draft
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Poll the backend's media-job proxy until the kie.ai job finishes. Backend
// also persists the resulting media_url on success, so we just need the URL
// back to update local state.
async function pollJob(postId: string, jobId: string): Promise<string> {
  const deadline = Date.now() + 5 * 60_000; // 5 min cap
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 4000));
    const job = await api<{ status: string; url?: string; error?: string }>(
      `/api/admin/ig-posts/${postId}/media-job/${encodeURIComponent(jobId)}`,
    );
    if (job.status === 'complete' && job.url) return job.url;
    if (job.status === 'failed') {
      throw new Error(job.error || 'media-service job failed');
    }
  }
  throw new Error('regen timed out after 5 minutes');
}
